import { useState, useRef, useEffect } from "react";
import { Download, Instagram, Share2, Trophy, Medal, Crown, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import html2canvas from "html2canvas";

const TEMPLATES = {
  default: {
    name: "Klasik",
    bgGradient: "linear-gradient(180deg, #0B1622 0%, #132337 50%, #1A3652 100%)",
    accentColor: "#4DFFFF",
    textColor: "#FFFFFF"
  },
  vibrant: {
    name: "Canlı",
    bgGradient: "linear-gradient(180deg, #1a0533 0%, #2d1b4e 50%, #4a1942 100%)",
    accentColor: "#FFD700",
    textColor: "#FFFFFF"
  },
  minimal: {
    name: "Minimal",
    bgGradient: "linear-gradient(180deg, #1C1C1E 0%, #2C2C2E 50%, #3C3C3E 100%)",
    accentColor: "#00FF88",
    textColor: "#FFFFFF"
  }
};

const getRankIcon = (rank) => {
  if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="text-sm font-bold text-gray-400">{rank}</span>;
};

const getRankBg = (rank, accentColor) => {
  if (rank === 1) return `linear-gradient(90deg, ${accentColor}20 0%, transparent 100%)`;
  if (rank === 2) return "linear-gradient(90deg, rgba(192,192,192,0.15) 0%, transparent 100%)";
  if (rank === 3) return "linear-gradient(90deg, rgba(205,127,50,0.15) 0%, transparent 100%)";
  return "transparent";
};

export const InstagramStoryExport = ({ 
  type = "week", // "week" | "league" | "tournament"
  data,
  onClose,
  isOpen
}) => {
  const [template, setTemplate] = useState("default");
  const [isExporting, setIsExporting] = useState(false);
  const storyRef = useRef(null);
  const currentTemplate = TEMPLATES[template];

  if (!isOpen || !data) return null;

  const handleExport = async () => {
    if (!storyRef.current) return;
    
    setIsExporting(true);
    try {
      const canvas = await html2canvas(storyRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: 1080 / 2,
        height: 1920 / 2
      });
      
      const link = document.createElement("a");
      link.download = `kahveligi-story-${type}-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Export error:", error);
    }
    setIsExporting(false);
  };

  const renderWeekStory = () => (
    <div 
      ref={storyRef}
      className="relative overflow-hidden"
      style={{
        width: "540px",
        height: "960px",
        background: currentTemplate.bgGradient
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full" 
          style={{ background: currentTemplate.accentColor, filter: "blur(60px)" }} />
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full" 
          style={{ background: currentTemplate.accentColor, filter: "blur(80px)" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ background: `${currentTemplate.accentColor}20`, border: `1px solid ${currentTemplate.accentColor}40` }}>
            <Trophy className="w-5 h-5" style={{ color: currentTemplate.accentColor }} />
            <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: currentTemplate.accentColor }}>
              Hafta {data.week_number}
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: currentTemplate.textColor }}>
            {data.league?.name}
          </h1>
          <p className="text-lg opacity-70" style={{ color: currentTemplate.textColor }}>
            {data.game?.name_tr} • {data.date?.split("T")[0]}
          </p>
        </div>

        {/* AI Headline */}
        {data.ai_headline && (
          <div className="text-center mb-6 px-4">
            <p className="text-xl font-medium italic" style={{ color: currentTemplate.accentColor }}>
              "{data.ai_headline}"
            </p>
          </div>
        )}

        {/* Rankings */}
        <div className="flex-1 space-y-3">
          {data.players?.slice(0, 5).map((player, index) => (
            <div 
              key={player.user_id}
              className="flex items-center gap-4 p-4 rounded-2xl"
              style={{ background: getRankBg(player.rank, currentTemplate.accentColor) }}
            >
              <div className="w-10 h-10 flex items-center justify-center">
                {getRankIcon(player.rank)}
              </div>
              {player.picture ? (
                <img 
                  src={player.picture} 
                  alt={player.name}
                  className="w-12 h-12 rounded-full object-cover border-2"
                  style={{ borderColor: player.rank === 1 ? currentTemplate.accentColor : "transparent" }}
                />
              ) : (
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                  style={{ 
                    background: `${currentTemplate.accentColor}30`,
                    color: currentTemplate.textColor
                  }}
                >
                  {player.name?.charAt(0) || "?"}
                </div>
              )}
              <div className="flex-1">
                <p className="font-semibold text-lg" style={{ color: currentTemplate.textColor }}>
                  {player.name}
                </p>
                <p className="text-sm opacity-60" style={{ color: currentTemplate.textColor }}>
                  @{player.username}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: currentTemplate.accentColor }}>
                  {player.points}
                </p>
                <p className="text-xs opacity-50" style={{ color: currentTemplate.textColor }}>puan</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg"
              alt="Kahve LİGİ"
              className="w-10 h-10 rounded-lg"
            />
            <span className="text-xl font-bold" style={{ color: currentTemplate.accentColor }}>
              KAHVE LİGİ
            </span>
          </div>
          <p className="text-sm opacity-50" style={{ color: currentTemplate.textColor }}>
            #KahveLigi #{data.game?.name?.replace(/\s/g, "")}
          </p>
        </div>
      </div>
    </div>
  );

  const renderLeagueStory = () => (
    <div 
      ref={storyRef}
      className="relative overflow-hidden"
      style={{
        width: "540px",
        height: "960px",
        background: currentTemplate.bgGradient
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 right-10 w-48 h-48 rounded-full" 
          style={{ background: currentTemplate.accentColor, filter: "blur(80px)" }} />
        <div className="absolute bottom-40 left-10 w-32 h-32 rounded-full" 
          style={{ background: currentTemplate.accentColor, filter: "blur(60px)" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ background: `${currentTemplate.accentColor}20`, border: `1px solid ${currentTemplate.accentColor}40` }}>
            <Trophy className="w-5 h-5" style={{ color: currentTemplate.accentColor }} />
            <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: currentTemplate.accentColor }}>
              Genel Sıralama
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: currentTemplate.textColor }}>
            {data.league?.name}
          </h1>
          <p className="text-lg opacity-70" style={{ color: currentTemplate.textColor }}>
            {data.game?.name_tr} • {data.total_weeks} Hafta
          </p>
        </div>

        {/* Standings */}
        <div className="flex-1 space-y-3">
          {data.standings?.slice(0, 6).map((player) => (
            <div 
              key={player.user_id}
              className="flex items-center gap-4 p-4 rounded-2xl"
              style={{ background: getRankBg(player.rank, currentTemplate.accentColor) }}
            >
              <div className="w-10 h-10 flex items-center justify-center">
                {getRankIcon(player.rank)}
              </div>
              {player.picture ? (
                <img 
                  src={player.picture} 
                  alt={player.name}
                  className="w-12 h-12 rounded-full object-cover border-2"
                  style={{ borderColor: player.rank === 1 ? currentTemplate.accentColor : "transparent" }}
                />
              ) : (
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                  style={{ 
                    background: `${currentTemplate.accentColor}30`,
                    color: currentTemplate.textColor
                  }}
                >
                  {player.name?.charAt(0) || "?"}
                </div>
              )}
              <div className="flex-1">
                <p className="font-semibold text-lg" style={{ color: currentTemplate.textColor }}>
                  {player.name}
                </p>
                <p className="text-sm opacity-60" style={{ color: currentTemplate.textColor }}>
                  {player.weeks_played} hafta
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: currentTemplate.accentColor }}>
                  {player.total_points}
                </p>
                <p className="text-xs opacity-50" style={{ color: currentTemplate.textColor }}>puan</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg"
              alt="Kahve LİGİ"
              className="w-10 h-10 rounded-lg"
            />
            <span className="text-xl font-bold" style={{ color: currentTemplate.accentColor }}>
              KAHVE LİGİ
            </span>
          </div>
          <p className="text-sm opacity-50" style={{ color: currentTemplate.textColor }}>
            #KahveLigi #{data.game?.name?.replace(/\s/g, "")}
          </p>
        </div>
      </div>
    </div>
  );

  const renderTournamentStory = () => (
    <div 
      ref={storyRef}
      className="relative overflow-hidden"
      style={{
        width: "540px",
        height: "960px",
        background: currentTemplate.bgGradient
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full" 
          style={{ background: currentTemplate.accentColor, filter: "blur(100px)" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ background: `${currentTemplate.accentColor}20`, border: `1px solid ${currentTemplate.accentColor}40` }}>
            <Trophy className="w-5 h-5" style={{ color: currentTemplate.accentColor }} />
            <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: currentTemplate.accentColor }}>
              Turnuva
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: currentTemplate.textColor }}>
            {data.tournament?.name}
          </h1>
          <p className="text-lg opacity-70" style={{ color: currentTemplate.textColor }}>
            {data.game?.name_tr} • {data.players?.length} Katılımcı
          </p>
        </div>

        {/* Winner or Bracket Preview */}
        {data.winner ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="text-center mb-8">
              <Crown className="w-20 h-20 mx-auto mb-4" style={{ color: "#FFD700" }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: currentTemplate.accentColor }}>
                ŞAMPİYON
              </h2>
            </div>
            {data.winner.picture ? (
              <img 
                src={data.winner.picture}
                alt={data.winner.name}
                className="w-32 h-32 rounded-full object-cover border-4 mb-4"
                style={{ borderColor: currentTemplate.accentColor }}
              />
            ) : (
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold mb-4"
                style={{ 
                  background: `${currentTemplate.accentColor}30`,
                  color: currentTemplate.textColor
                }}
              >
                {data.winner.name?.charAt(0) || "?"}
              </div>
            )}
            <h3 className="text-3xl font-bold mb-2" style={{ color: currentTemplate.textColor }}>
              {data.winner.name}
            </h3>
            <p className="text-lg opacity-60" style={{ color: currentTemplate.textColor }}>
              @{data.winner.username}
            </p>
          </div>
        ) : (
          <div className="flex-1 space-y-3">
            <h2 className="text-xl font-bold text-center mb-4" style={{ color: currentTemplate.accentColor }}>
              Katılımcılar
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {data.players?.slice(0, 8).map((player) => (
                <div 
                  key={player.user_id}
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{ background: `${currentTemplate.accentColor}10` }}
                >
                  {player.picture ? (
                    <img 
                      src={player.picture}
                      alt={player.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{ 
                        background: `${currentTemplate.accentColor}30`,
                        color: currentTemplate.textColor
                      }}
                    >
                      {player.name?.charAt(0) || "?"}
                    </div>
                  )}
                  <span className="text-sm font-medium truncate" style={{ color: currentTemplate.textColor }}>
                    {player.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg"
              alt="Kahve LİGİ"
              className="w-10 h-10 rounded-lg"
            />
            <span className="text-xl font-bold" style={{ color: currentTemplate.accentColor }}>
              KAHVE LİGİ
            </span>
          </div>
          <p className="text-sm opacity-50" style={{ color: currentTemplate.textColor }}>
            #KahveLigi #Turnuva
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-[#0B1622] rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-[#0B1622] border-b border-white/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Instagram className="w-6 h-6 text-pink-500" />
            <h2 className="text-xl font-bold text-white">Instagram Story Dışa Aktar</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6">
          {/* Template Selector */}
          <div className="mb-6">
            <label className="text-sm text-gray-400 mb-2 block">Şablon Seç</label>
            <div className="flex gap-3">
              {Object.entries(TEMPLATES).map(([key, tmpl]) => (
                <button
                  key={key}
                  onClick={() => setTemplate(key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    template === key 
                      ? "bg-[#4DFFFF] text-[#0B1622]" 
                      : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  {tmpl.name}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="flex justify-center mb-6">
            <div className="transform scale-[0.6] origin-top">
              {type === "week" && renderWeekStory()}
              {type === "league" && renderLeagueStory()}
              {type === "tournament" && renderTournamentStory()}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleExport}
              disabled={isExporting}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-3"
              data-testid="export-story-btn"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  İndiriliyor...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  PNG İndir (1080x1920)
                </>
              )}
            </Button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-4">
            İndirilen görüntüyü Instagram Story'de paylaşabilirsiniz
          </p>
        </div>
      </div>
    </div>
  );
};

export default InstagramStoryExport;
